import type { Transport, TransportResult, TurnPayload } from "./types.js";

/**
 * Maps the score keeper's HTTP API onto the queue's three outcomes.
 *
 * The classification is the whole point: getting it wrong either loses a turn
 * (treating a retryable failure as permanent) or wedges the queue forever
 * (treating a permanent refusal as retryable).
 */

export interface TransportOptions {
  baseUrl?: string;
  /** Called for each request; returns the current device token, if any. */
  token: () => string | null;
  fetch?: typeof globalThis.fetch;
}

interface ErrorBody {
  error?: string;
  /** Present on a seq conflict: where the server's log actually is. */
  expectedSeq?: number;
}

export function httpTransport(options: TransportOptions): Transport {
  const base = options.baseUrl ?? "/api/kniffel";
  // Bound: a browser's fetch throws "Illegal invocation" if it is called as a
  // method of anything other than the window. See session.ts.
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);

  return async function send(gameId: string, payload: TurnPayload): Promise<TransportResult> {
    const token = options.token();
    if (!token) {
      // Not signed in yet. The turn stays queued; this is not the user's fault
      // and definitely not a reason to discard it.
      return { ok: false, permanent: false, error: "not_signed_in" };
    }

    let response: Response;
    try {
      response = await doFetch(`${base}/games/${gameId}/turns`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
    } catch {
      // No network, DNS failure, timeout: precisely what the queue exists for.
      return { ok: false, permanent: false, error: "network" };
    }

    // 201 created, or 200 for a turn the server already had -- the client id
    // makes a replay idempotent, so both mean "the server has it".
    if (response.ok) return { ok: true };

    const body = await readError(response);

    if (response.status === 409) {
      // A stale seq is recoverable by renumbering. Any other conflict --
      // the game is already finished, say -- will read the same on every
      // retry, so queueing it forever would only wedge everything behind it.
      return typeof body.expectedSeq === "number"
        ? { ok: false, rebase: body.expectedSeq, error: body.error ?? "seq_conflict" }
        : { ok: false, permanent: true, error: body.error ?? "conflict" };
    }
    if (response.status === 422) {
      // The server will never accept this turn, however many times we ask.
      return { ok: false, permanent: true, error: body.error ?? "rejected" };
    }
    if (response.status === 401) {
      // The token expired or was revoked. Signing in again fixes it; dropping
      // the turn would not.
      return { ok: false, permanent: false, error: "unauthenticated" };
    }
    if (response.status === 404) {
      // The game is not on the server -- yet. It may simply not have been
      // created before this turn was tried (a race between reconcile and
      // flush), so this is retryable: the turn waits for its game rather than
      // being thrown away. A game genuinely deleted elsewhere leaves its turns
      // pending, which is honest -- they never landed -- and clearable by hand.
      return { ok: false, permanent: false, error: body.error ?? "unknown_game" };
    }
    // 5xx and anything unforeseen: assume the server can recover.
    return { ok: false, permanent: false, error: body.error ?? `http_${response.status}` };
  };
}

async function readError(response: Response): Promise<ErrorBody> {
  try {
    const parsed: unknown = await response.json();
    return typeof parsed === "object" && parsed !== null ? (parsed) : {};
  } catch {
    // An error page rather than JSON; the status code is enough to classify it.
    return {};
  }
}
